import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
 Upload,
 FileText,
 CheckCircle,
 Download,
 User,
 Briefcase,
 Zap,
} from 'lucide-react';
import { TerminalLoader } from '@/components/ui/TerminalLoader';
import { useOnboardingStore } from '@/stores/onboardingStore';
import { extractTextFromPDF } from '@/utils/pdfParser';
import { ImprovedOnboardingLayout } from '../ImprovedOnboardingLayout';
import type { ParsedResumeData } from '@/types/onboarding';

interface ImprovedResumeUploadStepProps {
 onDataExtracted: (data: ParsedResumeData) => void;
 onNext: () => void;
 onPrevious?: () => void;
}

export function ImprovedResumeUploadStep({
 onDataExtracted,
 onNext,
 onPrevious,
}: ImprovedResumeUploadStepProps) {
 const { toast } = useToast();
 const [uploadedFile, setUploadedFile] = useState<File | null>(null);
 const {
  parseResumeText,
  isLoading: isParsing,
  error: storeError,
  resumeData: parsedData,
 } = useOnboardingStore();
 const [success, setSuccess] = useState(false);

 const onDrop = useCallback(
  async (acceptedFiles: File[]) => {
   const file = acceptedFiles[0];
   if (!file) return;

   setUploadedFile(file);
   setSuccess(false);

   try {
    console.log('[Resume Upload] Starting upload for file:', file.name);
    const extractedText = await extractTextFromPDF(file);
    const data = await parseResumeText(extractedText);
    setSuccess(true);
    onDataExtracted(data);
   } catch (err: any) {
    toast({
     title: 'Upload Failed',
     description: err.message || 'Failed to parse resume',
     variant: 'destructive',
    });
   }
  },
  [onDataExtracted, parseResumeText, toast]
 );

 const { getRootProps, getInputProps, isDragActive } = useDropzone({
  onDrop,
  accept: {
   'application/pdf': ['.pdf'],
  },
  maxFiles: 1,
  maxSize: 10 * 1024 * 1024,
 });

 const handleSkip = () => onNext();
 const handleRetry = () => {
  setUploadedFile(null);
  setSuccess(false);
 };
 const handleContinue = () => onNext();

 return (
  <ImprovedOnboardingLayout
   currentStep={1}
   totalSteps={3}
   onNext={success ? handleContinue : undefined}
   onPrevious={onPrevious}
   isNextDisabled={!success}
   isLoading={isParsing}
   nextButtonText={success ? 'Continue to Profile Review' : 'Continue'}
   showSkip={!success && !isParsing}
   onSkip={handleSkip}
  >
   <div className='space-y-8'>
    <Card className='rounded-none border-2 border-dashed border-border hover:border-primary/50 transition-colors bg-secondary/5'>
     <CardContent className='p-8'>
      {!uploadedFile && !success && (
       <div
        {...getRootProps()}
        className={`text-center cursor-pointer transition-all duration-200 ${
         isDragActive ? 'scale-105 bg-primary/5' : 'hover:bg-primary/5'
        }`}
       >
        <input {...getInputProps()} />
        <div className='mx-auto w-24 h-24 bg-primary/10 flex items-center justify-center mb-6 border border-primary/20'>
         <Upload className='h-12 w-12 text-primary' />
        </div>
        <div className='space-y-4'>
         <h3 className='text-2xl font-bold font-mono uppercase tracking-wider text-primary'>
          RESUME_UPLOAD
         </h3>
         <p className='text-muted-foreground text-sm font-mono'>
          &gt; drag_and_drop_or_click_to_browse
         </p>
         <div className='flex items-center justify-center space-x-6 text-xs text-muted-foreground font-mono uppercase tracking-wide'>
          <div className='flex items-center space-x-2'>
           <FileText className='h-4 w-4' />
           <span>PDF_ONLY</span>
          </div>
          <div className='flex items-center space-x-2'>
           <Download className='h-4 w-4' />
           <span>MAX_10MB</span>
          </div>
         </div>
         <Button
          size='lg'
          className='mt-4 rounded-none font-mono uppercase tracking-widest'
         >
          SELECT_FILE
         </Button>
        </div>
       </div>
      )}

      {uploadedFile && !success && (
       <div className='space-y-6'>
        <div className='flex items-center space-x-4 p-4 bg-secondary/5 border border-border/50'>
         <div className='w-12 h-12 bg-primary/10 flex items-center justify-center border border-primary/20'>
          <FileText className='h-6 w-6 text-primary' />
         </div>
         <div className='flex-1 font-mono'>
          <h4 className='font-bold text-sm uppercase tracking-wide'>
           {uploadedFile.name}
          </h4>
          <p className='text-xs text-muted-foreground mt-1'>
           FILE_SIZE: {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
          </p>
         </div>
        </div>
        {isParsing && <TerminalLoader text='ANALYZING_DOCUMENT_STRUCTURE...' />}
       </div>
      )}

      {success && parsedData && (
       <div className='space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700'>
        <div className='p-4 border-l-2 border-green-500 bg-green-500/10 flex items-start gap-3'>
         <CheckCircle className='h-5 w-5 text-green-500 mt-0.5' />
         <div className='text-sm text-green-500 font-mono uppercase tracking-wide'>
          SUCCESS: RESUME_PARSED_SUCCESSFULLY
          <br />
          <span className='text-xs opacity-80 normal-case'>
           Professional data extraction complete.
          </span>
         </div>
        </div>

        <div className='grid md:grid-cols-3 gap-4'>
         <Card className='rounded-none border-border bg-card'>
          <CardHeader className='pb-3'>
           <CardTitle className='flex items-center space-x-2 text-primary font-mono text-sm uppercase tracking-wider'>
            <User className='h-4 w-4' />
            <span>Personal_Data</span>
           </CardTitle>
          </CardHeader>
          <CardContent className='space-y-2 text-xs font-mono text-muted-foreground'>
           <div>
            <span className='text-foreground uppercase'>Name:</span>{' '}
            {parsedData.personalInfo.name}
           </div>
           <div>
            <span className='text-foreground uppercase'>Email:</span>{' '}
            {parsedData.personalInfo.email}
           </div>
          </CardContent>
         </Card>
         <Card className='rounded-none border-border bg-card'>
          <CardHeader className='pb-3'>
           <CardTitle className='flex items-center space-x-2 text-primary font-mono text-sm uppercase tracking-wider'>
            <Briefcase className='h-4 w-4' />
            <span>Experience</span>
           </CardTitle>
          </CardHeader>
          <CardContent className='space-y-2 text-xs font-mono text-muted-foreground'>
           {parsedData.currentRole && (
            <div>
             <span className='text-foreground uppercase'>Current:</span>{' '}
             {parsedData.currentRole.title}
            </div>
           )}
          </CardContent>
         </Card>
         <Card className='rounded-none border-border bg-card'>
          <CardHeader className='pb-3'>
           <CardTitle className='flex items-center space-x-2 text-primary font-mono text-sm uppercase tracking-wider'>
            <Zap className='h-4 w-4' />
            <span>Extraction_Stats</span>
           </CardTitle>
          </CardHeader>
          <CardContent className='space-y-2 text-xs font-mono text-muted-foreground'>
           <div>
            <span className='text-foreground uppercase'>Jobs_Parsed:</span>{' '}
            {parsedData.experience?.length || 0}
           </div>
           <div>
            <span className='text-foreground uppercase'>
             Skills_Identified:
            </span>{' '}
            {parsedData.skills?.length || 0}
           </div>
           <div>
            <span className='text-foreground uppercase'>
             Projects_Extracted:
            </span>{' '}
            {parsedData.projects?.length || 0}
           </div>
           <div>
            <span className='text-foreground uppercase'>
             Education_History:
            </span>{' '}
            {parsedData.education?.length || 0}
           </div>
          </CardContent>
         </Card>
        </div>
       </div>
      )}

      {storeError && !isParsing && (
       <div className='space-y-4'>
        <div className='text-center space-y-3'>
         <p className='text-sm text-muted-foreground font-mono'>
          &gt; UPLOAD_FAILED: {storeError}
         </p>
         <div className='flex justify-center space-x-3'>
          <Button
           variant='outline'
           onClick={handleRetry}
           className='rounded-none font-mono uppercase'
          >
           RETRY_UPLOAD
          </Button>
          <Button
           variant='ghost'
           onClick={handleSkip}
           className='rounded-none font-mono uppercase'
          >
           SKIP_AND_ENTER_MANUALLY
          </Button>
         </div>
        </div>
       </div>
      )}
     </CardContent>
    </Card>
   </div>
  </ImprovedOnboardingLayout>
 );
}
